import React, { useState, useCallback } from "react";
import { ConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialog";
import { Typography } from "@mui/material";
import i18n from "i18next";

export interface ConfirmOptions {
	title?: string;
	message?: string;
}

const DEFAULT_TITLE = i18n.t("are_you_sure");

const DEFAULT_CONFIRM_OPTIONS = {
	title: DEFAULT_TITLE,
};

export type ShowConfirmDialogFunc = (options?: ConfirmOptions) => Promise<boolean>;
type ResolveFunction = (value: boolean | PromiseLike<boolean>) => void;

export const ConfirmDialogContext = React.createContext<ShowConfirmDialogFunc | undefined>(undefined);

export interface ConfirmProviderProps extends React.PropsWithChildren {}

export const ConfirmDialogProvider = ({ children }: ConfirmProviderProps) => {
	const [options, setOptions] = useState<ConfirmOptions>(DEFAULT_CONFIRM_OPTIONS);
	const [resolveFuncList, setResolveFuncList] = useState<[ResolveFunction] | []>([]);
	const resolveFunc = resolveFuncList[0];

	const confirm = (o: ConfirmOptions = DEFAULT_CONFIRM_OPTIONS) => {
		return new Promise<boolean>((resolve) => {
			setOptions({
				title: o.title ?? DEFAULT_TITLE,
				message: o.message ?? "",
			});
			setResolveFuncList([resolve]);
		});
	};

	const closeDialog = useCallback(() => {
		setResolveFuncList([]);
	}, [setResolveFuncList]);

	const onCancel = useCallback(() => {
		resolveFunc?.(false);
		closeDialog();
	}, [resolveFunc, closeDialog]);

	const onConfirm = useCallback(() => {
		resolveFunc?.(true);
		closeDialog();
	}, [resolveFunc, closeDialog]);

	return (
		<>
			<ConfirmDialogContext.Provider value={confirm}>
				{children}
			</ConfirmDialogContext.Provider>
			{resolveFunc !== undefined && (
				<ConfirmDialog onConfirm={onConfirm} title={options.title} onClose={onCancel}>
					<Typography
						sx={{
							whiteSpace: "pre-wrap",
						}}
					>{options.message}</Typography>
				</ConfirmDialog>
			)}
		</>
	);
};
